import { cn } from "@/lib/utils";
import { useScratchCanvas } from "./hooks";

interface ScratchCardProps {
  cardId: number;
  isOpen: boolean;
  onReveal: () => void;
  wonLabel: string;
  prize: string;
  shop: string;
  hint: string;
}

// The scratch card: the prize sits under a canvas foil that the visitor
// scratches off with a finger or mouse. The foil is decorative (aria-hidden);
// keyboard and screen reader users use the "Reveal" button beside it, and
// the prize stays hidden from them until the card is open.
const ScratchCard = ({
  cardId,
  isOpen,
  onReveal,
  wonLabel,
  prize,
  shop,
  hint,
}: ScratchCardProps) => {
  const {
    canvasRef,
    cardRef,
    showHint,
    onPointerDown,
    onPointerMove,
    onPointerEnd,
  } = useScratchCanvas({ cardId, isOpen, onReveal });

  return (
    <div
      ref={cardRef}
      className="relative mx-auto h-[190px] w-full max-w-[340px] overflow-hidden rounded-3xl bg-linear-135 from-tone-sun to-tone-peach shadow-lift"
    >
      <div
        aria-hidden={!isOpen}
        className="absolute inset-0 grid place-content-center gap-0.5 text-center"
      >
        <span className="text-xs font-extrabold tracking-[0.07em] text-tone-sun-ink uppercase">
          {wonLabel}
        </span>
        <span className="text-[2rem] font-bold tracking-[-1px]">{prize}</span>
        <span className="text-ink-muted">{shop}</span>
      </div>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        className={cn(
          "absolute inset-0 cursor-crosshair touch-none motion-safe:transition-opacity motion-safe:duration-500",
          isOpen && "pointer-events-none opacity-0",
        )}
      />
      {showHint && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid place-items-center text-[1.2rem] font-extrabold tracking-[0.04em] text-ink-muted"
        >
          {hint}
        </span>
      )}
    </div>
  );
};

export default ScratchCard;
